import { createContext, useContext, useState, useMemo, useCallback } from "react";

export const translations = {
  en: {
    nav: {
      about: "House",
      services: "Services",
      experience: "Experience",
      instagram: "Journal",
      contact: "Contact",
      request: "Enquire",
    },
    hero: {
      kicker: "Villa Care · Ibiza",
      tagline: "Luxury Villa Care & Full-Service Hospitality",
      body: "Quiet, attentive villa care for Ibiza's most considered homes.",
      ctaPrimary: "Enquire",
      ctaSecondary: "WhatsApp",
      scroll: "Scroll",
    },
    about: {
      kicker: "The House",
      title: "More than a clean. A reset.",
      p1: "Villa Reset is a small, hands-on hospitality house based in Ibiza. We look after a select group of private villas, retreats and homes across the island — quietly, and with care.",
      p2: "Our work begins before anyone arrives and continues long after they've left.",
    },
    pillars: [
      {
        kicker: "01 — Discreet",
        title: "Discreet",
        body: "We work quietly around your guests — or your villa when it sits empty. Either way, nothing is rushed and nothing is announced.",
      },
      {
        kicker: "02 — Local",
        title: "Built for Ibiza",
        body: "Late arrivals, short turnarounds, long summers. Our team knows the island's rhythm and answers it without drama.",
      },
      {
        kicker: "03 — Complete",
        title: "Full-Service",
        body: "Cleaning, linen, atmosphere, groceries, concierge — what's needed is taken care of, end to end.",
      },
    ],
    services: {
      kicker: "What We Look After",
      title: "Curated villa support.",
      sub: "Ten things we do well. One standard across all of them.",
      list: [
        { title: "Villa Reset", desc: "Full preparation between stays — every room, every detail." },
        { title: "Deep Cleaning", desc: "Slower, more thorough work — top to bottom." },
        { title: "Guest Arrival Preparation", desc: "Atmosphere, scent and the small things, ready before arrival." },
        { title: "After-Party Recovery", desc: "Long nights handled. The villa quiet again by morning." },
        { title: "Pool & Outdoor Reset", desc: "Loungers, terraces and pool — ready for the next sunset." },
        { title: "Laundry Coordination", desc: "Pressed linen, towels and robes, looked after off-site." },
        { title: "Grocery Pre-Stocking", desc: "Fridge, bar and pantry, to your guests' preferences." },
        { title: "Retreat & Event Support", desc: "Operational support for private dinners, retreats and gatherings." },
        { title: "Concierge Assistance", desc: "Bookings, transfers, chefs — quietly arranged." },
        { title: "Daily Villa Care", desc: "Light, daily upkeep for long stays and resident owners." },
      ],
    },
    signature: {
      kicker: "The Experience",
      title: "Calm. Composed. Ready.",
      p: "Polished floors. Clean glass. Pressed linen. Styled spaces. The small things you don't notice — until they're not there.",
      p2: "A villa should feel calm and lived-in. Before arrival. Long after the night is over.",
    },
    ibiza: {
      kicker: "The Island",
      title: "An island rhythm, met with quiet precision.",
      p: "Guests arrive late. Plans change. The villa needs to be perfect again, sometimes within hours. We know Ibiza, and we answer it without fuss.",
    },
    instagram: {
      kicker: "Journal",
      title: "Follow the atmosphere.",
      handle: "@villaresetibiza",
      cta: "Visit Instagram",
    },
    contact: {
      kicker: "Enquiries",
      title: "Tell us about your villa.",
      sub: "Send a short note — dates, location, what's needed. We reply by email or WhatsApp, usually within the hour.",
      name: "Name",
      email: "Email",
      phone: "WhatsApp",
      location: "Villa Location",
      service: "Service Needed",
      servicePlaceholder: "Select a service",
      date: "Dates",
      message: "Message",
      messagePlaceholder: "Guests, timings, anything we should know.",
      send: "Send Enquiry",
      sending: "Sending…",
      success: "Thank you. We'll be in touch shortly.",
      error: "Something didn't go through — please try WhatsApp.",
      whatsapp: "WhatsApp",
      emailUs: "Email",
      instagram: "Instagram",
    },
    footer: {
      tagline: "Quiet, attentive villa care across Ibiza.",
      location: "Ibiza · Balearic Islands",
      rights: "All rights reserved.",
    },
  },

  es: {
    nav: { about: "Filosofía", services: "Servicios", experience: "Experiencia", instagram: "Journal", contact: "Contacto", request: "Solicitar" },
    hero: {
      kicker: "Cuidado de Villas de Lujo · Ibiza",
      tagline: "Cuidado de Villas de Lujo y Hospitalidad Integral",
      body: "Apoyo discreto diseñado para los espacios más bellos de Ibiza.",
      ctaPrimary: "Solicitar Servicio",
      ctaSecondary: "WhatsApp",
      scroll: "Desliza",
    },
    about: {
      kicker: "Filosofía",
      title: "Más que limpieza. Un reset completo.",
      p1: "Villa Reset Ibiza es una casa de hospitalidad discreta e integral, creada para villas privadas, retiros y huéspedes exigentes en toda la isla.",
      p2: "Preparamos, restauramos y cuidamos el espacio — en silencio, con precisión, por completo.",
    },
    pillars: [
      { kicker: "01 — Servicio Discreto", title: "Servicio Discreto", body: "Cuidado profesional de villas con precisión, rapidez y respeto por el espacio." },
      { kicker: "02 — Hecho para Ibiza", title: "Hecho para Ibiza", body: "Desde la llegada de invitados hasta la recuperación post-evento y la preparación completa — diseñado para el ritmo de la isla." },
      { kicker: "03 — Hospitalidad Integral", title: "Hospitalidad Integral", body: "Limpieza, ambiente, preparación y soporte — gestionados con fluidez." },
    ],
    services: {
      kicker: "Los Servicios",
      title: "Soporte de villa curado.",
      sub: "Diez servicios, un solo estándar.",
      list: [
        { title: "Villa Reset", desc: "La preparación completa entre estancias." },
        { title: "Limpieza Profunda", desc: "Precisión de arriba a abajo en toda la villa." },
        { title: "Llegada de Invitados", desc: "Ambiente, aroma y detalle listos antes de la llegada." },
        { title: "Recuperación Post-Fiesta", desc: "Las noches resueltas. Calma al amanecer." },
        { title: "Piscina y Exteriores", desc: "Tumbonas, terrazas y piscina, listas para el atardecer." },
        { title: "Coordinación de Lavandería", desc: "Lencería, toallas y batas planchados — con discreción." },
        { title: "Aprovisionamiento", desc: "Nevera, bar y despensa al gusto de los invitados." },
        { title: "Retiros y Eventos", desc: "Soporte operativo para cenas, retiros y reuniones." },
        { title: "Concierge", desc: "Reservas, traslados y presentaciones discretas." },
        { title: "Cuidado Diario", desc: "Mantenimiento silencioso para estancias largas y propietarios." },
      ],
    },
    signature: {
      kicker: "La Experiencia",
      title: "Calma. Compuesta. Lista.",
      p: "Suelos frescos. Cristal perfecto. Lencería planchada. Espacios estilizados. Exteriores listos para el atardecer.",
      p2: "La villa debe sentirse calma, fresca y viva — antes de la llegada y mucho después de la noche.",
    },
    ibiza: {
      kicker: "La Isla",
      title: "El ritmo de la isla, con precisión silenciosa.",
      p: "Los invitados llegan, las noches se despliegan, las villas deben volver a estar perfectas — a veces en pocas horas. Entendemos el ritmo de Ibiza y respondemos con discreción y rapidez.",
    },
    instagram: {
      kicker: "Journal",
      title: "Sigue la atmósfera.",
      handle: "@villaresetibiza",
      cta: "Ver Instagram",
    },
    contact: {
      kicker: "Solicitud",
      title: "Solicitar Servicio",
      sub: "Cuéntanos sobre tu villa. Respondemos en horas — a menudo en minutos.",
      name: "Nombre",
      email: "Email",
      phone: "WhatsApp",
      location: "Ubicación de la Villa",
      service: "Servicio Necesario",
      servicePlaceholder: "Elige un servicio",
      date: "Fechas",
      message: "Mensaje",
      messagePlaceholder: "Número de invitados, horarios, peticiones especiales…",
      send: "Enviar",
      sending: "Enviando…",
      success: "Solicitud recibida. Te contactaremos pronto.",
      error: "Algo salió mal. Por favor escríbenos por WhatsApp.",
      whatsapp: "WhatsApp",
      emailUs: "Email",
      instagram: "Instagram",
    },
    footer: { tagline: "Cuidado de Villas de Lujo y Hospitalidad Integral", location: "Ibiza, España", rights: "Todos los derechos reservados." },
  },

  fr: {
    nav: { about: "Philosophie", services: "Services", experience: "Expérience", instagram: "Journal", contact: "Contact", request: "Demander" },
    hero: {
      kicker: "Entretien de Villas de Luxe · Ibiza",
      tagline: "Entretien de Villas de Luxe & Hospitalité Intégrale",
      body: "Un soutien discret pensé pour les plus beaux espaces d'Ibiza.",
      ctaPrimary: "Demander un Service",
      ctaSecondary: "WhatsApp",
      scroll: "Défiler",
    },
    about: {
      kicker: "Philosophie",
      title: "Plus qu'un nettoyage. Un reset complet.",
      p1: "Villa Reset Ibiza est une maison d'hospitalité discrète et intégrale, créée pour les villas privées, les retraites et les invités exigeants à travers l'île.",
      p2: "Nous préparons, restaurons et veillons sur l'espace — silencieusement, précisément, complètement.",
    },
    pillars: [
      { kicker: "01 — Service Discret", title: "Service Discret", body: "Un soin professionnel des villas avec précision, rapidité et respect de l'espace." },
      { kicker: "02 — Pensé pour Ibiza", title: "Pensé pour Ibiza", body: "De l'arrivée des invités à la récupération post-événement — pensé pour le rythme de l'île." },
      { kicker: "03 — Hospitalité Intégrale", title: "Hospitalité Intégrale", body: "Nettoyage, ambiance, préparation et support — gérés avec fluidité." },
    ],
    services: {
      kicker: "Les Services",
      title: "Un soutien sur mesure.",
      sub: "Dix services, un seul standard.",
      list: [
        { title: "Villa Reset", desc: "La préparation complète entre les séjours." },
        { title: "Nettoyage Profond", desc: "Précision de haut en bas dans toute la villa." },
        { title: "Arrivée des Invités", desc: "Ambiance, parfum et détails prêts avant l'arrivée." },
        { title: "Récupération After-Party", desc: "Les nuits gérées. Calme retrouvé au matin." },
        { title: "Piscine & Extérieurs", desc: "Transats, terrasses et piscine prêts pour le coucher de soleil." },
        { title: "Coordination du Linge", desc: "Linge, serviettes et peignoirs repassés — avec discrétion." },
        { title: "Pré-Stockage", desc: "Frigo, bar et garde-manger au goût des invités." },
        { title: "Retraites & Événements", desc: "Support opérationnel pour dîners, retraites et réceptions." },
        { title: "Conciergerie", desc: "Réservations, transferts et présentations discrètes." },
        { title: "Soin Quotidien", desc: "Entretien silencieux pour longs séjours et propriétaires." },
      ],
    },
    signature: {
      kicker: "L'Expérience",
      title: "Calme. Composée. Prête.",
      p: "Sols frais. Vitres parfaites. Linge repassé. Espaces stylisés. Extérieurs prêts pour le coucher du soleil.",
      p2: "La villa doit être calme, fraîche et vivante — avant l'arrivée et bien après la nuit.",
    },
    ibiza: {
      kicker: "L'Île",
      title: "Le rythme de l'île, avec une précision silencieuse.",
      p: "Les invités arrivent, les soirées se déploient, les villas doivent redevenir parfaites — parfois en quelques heures. Nous comprenons le rythme d'Ibiza et y répondons avec discrétion et rapidité.",
    },
    instagram: {
      kicker: "Journal",
      title: "Suivez l'atmosphère.",
      handle: "@villaresetibiza",
      cta: "Voir Instagram",
    },
    contact: {
      kicker: "Demande",
      title: "Demander un Service",
      sub: "Parlez-nous de votre villa. Nous répondons en heures — souvent en minutes.",
      name: "Nom",
      email: "Email",
      phone: "WhatsApp",
      location: "Emplacement de la Villa",
      service: "Service Souhaité",
      servicePlaceholder: "Choisir un service",
      date: "Dates",
      message: "Message",
      messagePlaceholder: "Nombre d'invités, horaires, demandes spéciales…",
      send: "Envoyer",
      sending: "Envoi…",
      success: "Demande reçue. Nous revenons vers vous très vite.",
      error: "Une erreur est survenue. Merci de nous contacter via WhatsApp.",
      whatsapp: "WhatsApp",
      emailUs: "Email",
      instagram: "Instagram",
    },
    footer: { tagline: "Entretien de Villas de Luxe & Hospitalité Intégrale", location: "Ibiza, Espagne", rights: "Tous droits réservés." },
  },
};

const I18nContext = createContext(null);

export function I18nProvider({ children }) {
  const [lang, setLang] = useState("en");
  const t = useMemo(() => translations[lang], [lang]);
  const setLanguage = useCallback((l) => {
    if (translations[l]) setLang(l);
  }, []);
  const value = useMemo(() => ({ lang, setLang: setLanguage, t }), [lang, setLanguage, t]);
  return <I18nContext.Provider value={value}>{children}</I18nContext.Provider>;
}

export function useI18n() {
  const ctx = useContext(I18nContext);
  if (!ctx) throw new Error("useI18n must be used inside I18nProvider");
  return ctx;
}

export const CONTACT = {
  whatsappRaw: "4799082222",
  whatsappDisplay: "+47 990 82 222",
  email: "hello@villaresetibiza.com",
  instagram: "@villaresetibiza",
  instagramUrl: "https://instagram.com/villaresetibiza",
};

export const LOGO_URL = "/vr-logo.png";
export const LOGO_TRANSPARENT = "/vr-logo-transparent.png";

// Footer "Trusted by" — placeholder labels. Replace each with a real client/partner once you have one.
// Set to [] to hide the line entirely.
export const TRUSTED_BY = [
  "Private Villas",
  "Retreats",
  "Creative Studios",
];
